/**
 * Disease Matcher — detect disease/indication references in natural-language prompts.
 *
 * Runs BEFORE the LLM is called on the text-to-action pipeline. When a user
 * prompt references a disease we have seeded in diseaseTargets.js, we
 * short-circuit the LLM and return a structured clarification asking which
 * drug target the user wants to pursue. This prevents the LLM from
 * hallucinating actionIds like "design-drug-hepB" on abstract goals.
 *
 * Matching strategy: scan the prompt for any alias or canonicalName from
 * the disease table, with case-insensitive word-boundary matching, and
 * return the longest match (to prefer "hepatitis B virus infection" over
 * "hepatitis").
 *
 * @module gibbs/knowledge/diseaseMatcher
 */

import { diseaseTargets } from './diseaseTargets';

/**
 * @typedef {Object} DiseaseMatch
 * @property {string} slug - Disease slug key into diseaseTargets.diseases
 * @property {Object} entry - The disease entry from the table
 * @property {string} matchedAlias - The alias string that was found in the prompt
 * @property {number} matchLength - Length of the matched alias (for ranking)
 */

/**
 * Build the full list of (alias, slug) pairs from the disease table.
 * Includes the canonicalName as an alias too. Sorted by length descending
 * so longest matches are tried first.
 *
 * @returns {Array<{alias: string, lower: string, slug: string}>}
 */
function buildAliasIndex() {
    const index = [];
    const diseases = diseaseTargets.diseases || {};
    for (const [slug, entry] of Object.entries(diseases)) {
        const aliases = new Set();
        if (entry.canonicalName) aliases.add(entry.canonicalName);
        for (const a of entry.aliases || []) aliases.add(a);
        for (const alias of aliases) {
            index.push({
                alias,
                lower: alias.toLowerCase(),
                slug,
            });
        }
    }
    // Sort by length descending — longest aliases first
    index.sort((a, b) => b.lower.length - a.lower.length);
    return index;
}

// Built once at module load
const ALIAS_INDEX = buildAliasIndex();

/**
 * Check if a substring is surrounded by word boundaries in the full string.
 * We want "HIV" to match "HIV" but not "HIVE" or "archive".
 *
 * @param {string} haystack - Lowercased full prompt
 * @param {number} start - Match start index
 * @param {number} end - Match end index (exclusive)
 * @returns {boolean}
 */
function isWordBoundary(haystack, start, end) {
    const isWordChar = (c) => /[a-z0-9]/.test(c);
    const before = start > 0 ? haystack[start - 1] : ' ';
    const after = end < haystack.length ? haystack[end] : ' ';
    return !isWordChar(before) && !isWordChar(after);
}

/**
 * Scan a user prompt for any known disease alias.
 * Returns the longest match found, or null.
 *
 * @param {string} userMessage - Raw user prompt
 * @returns {DiseaseMatch | null}
 */
export function matchDisease(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return null;
    const lower = userMessage.toLowerCase();

    for (const entry of ALIAS_INDEX) {
        const start = lower.indexOf(entry.lower);
        if (start === -1) continue;
        const end = start + entry.lower.length;
        if (!isWordBoundary(lower, start, end)) continue;

        // Found a match — return it (ALIAS_INDEX is pre-sorted longest-first)
        return {
            slug: entry.slug,
            entry: diseaseTargets.diseases[entry.slug],
            matchedAlias: entry.alias,
            matchLength: entry.lower.length,
        };
    }
    return null;
}

/**
 * Escape-hatch: check whether the user's prompt looks like a design/discovery
 * goal at the disease level. Combines matchDisease() with a gate on the
 * prompt containing a goal-indicating verb ("design", "find", "develop",
 * "treat", "inhibit", etc.). This prevents us from intercepting bland
 * mentions like "hepatitis B is a virus" when the user is just asking a
 * chat question.
 *
 * @param {string} userMessage
 * @returns {DiseaseMatch | null} Match if this looks like a design goal
 */
export function matchDesignGoalForDisease(userMessage) {
    const match = matchDisease(userMessage);
    if (!match) return null;

    const lower = userMessage.toLowerCase();
    // 2026-06-02 (Gibbs router improvement): extended with workflow-
    // NAME-anchored intent phrases. The user's query "I want to run a
    // hit to lead on hep b. Please make a workflow" previously missed
    // tier 0a (no recognized goal verb), hit tier 0a2 (workflow-
    // template), and emitted a validation-failure message because the
    // template's pdbId was unresolved. Adding "run a hit to lead" (and
    // hyphen variants) gates the cascade in for these phrasings so it
    // can emit target_selection → PR #48 G2 fix returns a workflow
    // plan with pdbId queued.
    //
    // SCOPE: kept tight — only workflow-NAME-anchored phrases
    // ("run a hit to lead", "do a hit-to-lead"). Generic verbs
    // ("make a workflow", "want to do") would conflict with
    // anchor_resolver tests in clarifyOnAmbiguousTarget.integration.
    // test.js — those tests deliberately use generic workflow phrasing
    // WITHOUT a goal verb to exercise tier 0f's loaded-protein-
    // matching behavior (which the cascade doesn't replicate).
    const goalVerbs = [
        'design', 'develop', 'discover', 'find', 'treat', 'cure',
        'target', 'inhibit', 'block', 'relieve', 'make a drug',
        'want a drug', 'need a drug', 'need to find',
        // Workflow-name-anchored intent (2026-06-02)
        'run a hit to lead', 'do a hit to lead',
        'run a hit-to-lead', 'do a hit-to-lead',
    ];
    const hasGoalVerb = goalVerbs.some((v) => lower.includes(v));
    return hasGoalVerb ? match : null;
}
